import React from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import Checkbox from '../../../v2/forms/Checkbox';
import CargomaticSelect from '../../../v2/select';
import CargomaticInput from '../../../v2/input';
import stopIntentTypes from '../../../../config/stop-intent-types';
import { mapIntentToSlug, mapSlugToIntent, } from '../../../../utils/formatters/stops';
import LocationAutosuggest from '../../../site/location-autosuggest';
import DeleteButton from '../../../v2/buttons/DeleteButton';
import DateIconButton from '../../../v2/buttons/DateIconButton';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
export default function StopForm({ stop, onStopChange, canRemove, onRemove }) {
    const { t } = useTranslation();
    function handleIntentChange(item) {
        onStopChange(Object.assign(Object.assign({}, stop), mapSlugToIntent(item.slug)));
    }
    function renderIntentDropdown() {
        return (React.createElement("div", { className: "sm:px-4" },
            React.createElement(CargomaticSelect, { label: titleize(t('ACTION')), items: stopIntentTypes, textKey: "localizationSlug", valueKey: "slug", value: mapIntentToSlug(stop), onSelect: handleIntentChange })));
    }
    function handleLocationNameChange(name) {
        var _a;
        const location = [Object.assign(Object.assign({}, (((_a = stop === null || stop === void 0 ? void 0 : stop.location) === null || _a === void 0 ? void 0 : _a[0]) || {})), { name })];
        onStopChange(Object.assign(Object.assign({}, stop), { location }));
    }
    function renderLocationName() {
        const location = getStopLocation(stop);
        return (React.createElement("div", { className: "sm:px-4" },
            React.createElement(CargomaticInput, { name: "location-name", label: t('LOCATION_NAME'), value: location.name, placeholder: t('LOCATION_NAME'), onChange: handleLocationNameChange })));
    }
    function handleLocationAddressChange() {
        const location = [Object.assign(Object.assign({}, getStopLocation(stop)), { address: '' })];
        onStopChange(Object.assign(Object.assign({}, stop), { location }));
    }
    function handleLocationSelect(item) {
        const currentLocation = getStopLocation(stop);
        const name = currentLocation.name || item.name;
        const location = [Object.assign(Object.assign(Object.assign({}, currentLocation), item), { name })];
        onStopChange(Object.assign(Object.assign({}, stop), { location }));
    }
    function renderLocationAddress() {
        const location = getStopLocation(stop);
        const { name, address } = location;
        return (React.createElement("div", { className: "px-4" },
            React.createElement("label", { className: "form-label mb-1 block" }, t('ADDRESS')),
            React.createElement(LocationAutosuggest, { id: `location-${stop.sequence}`, name: name, address: address, value: address, suggestions: [
                    {
                        name,
                        address,
                    },
                ], callbackSelectedItem: handleLocationSelect, onChange: handleLocationAddressChange })));
    }
    function handleOffsiteChange(off_site) {
        onStopChange(Object.assign(Object.assign({}, stop), { off_site }));
    }
    function handleWindowDateChange(date) {
        onStopChange(Object.assign(Object.assign({}, stop), { window_date: date }));
    }
    function handleApplyEquipmentChange(applyEquipment) {
        onStopChange(Object.assign(Object.assign({}, stop), { applyEquipment }));
    }
    return (React.createElement("div", { className: "relative" },
        React.createElement("div", { className: "p-4" },
            React.createElement("div", { className: "flex flex-wrap mb-4 -mx-4" },
                React.createElement("div", { className: "px-4 bg-grey-darkest w-10 h-10 flex-shrink-0 flex-grow-0 rounded-full flex justify-center items-center text-white mb-4 sm:mb-0" }, stop.sequence),
                React.createElement("div", { className: "px-4" },
                    React.createElement("div", { className: "flex flex-wrap sm:-mx-4" },
                        renderIntentDropdown(),
                        renderLocationName(),
                        renderLocationAddress(),
                        React.createElement("div", { className: "sm:px-4" },
                            React.createElement("label", { htmlFor: "pickupDate", className: "block mb-2 form-label" }, t('APPOINTMENT_DATE')),
                            React.createElement(DatePicker, { id: "date", selected: getMomentDate(stop.window_date), onChange: handleWindowDateChange, customInput: React.createElement(DateIconButton, null) })),
                        React.createElement("div", { className: "sm:px-4" },
                            React.createElement(Checkbox, { checked: stop.off_site, onChange: handleOffsiteChange }, t('IS_OFFSITE'))),
                        React.createElement("div", { className: "sm:px-4" },
                            React.createElement(Checkbox, { checked: stop.applyEquipment, onChange: handleApplyEquipmentChange }, t('SHOULD_APPLY_EQUIPMENT')))))),
            canRemove && onRemove && (React.createElement("div", { className: "absolute top-4 right-0" },
                React.createElement(DeleteButton, { onClick: () => onRemove(stop) }))))));
}
StopForm.propTypes = {
    canRemove: PropTypes.bool,
    stop: PropTypes.shape({
        sequence: PropTypes.number.isRequired,
        action: PropTypes.oneOf(['pickup', 'deliver']),
        empty: PropTypes.bool.isRequired,
        location: PropTypes.shape({
            name: PropTypes.string,
            address: PropTypes.string,
        }),
        off_site: PropTypes.bool,
        applyEquipment: PropTypes.bool,
    }),
    onStopChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};
StopForm.defaultProps = {
    canRemove: true,
};
function getStopLocation(stop) {
    var _a;
    return ((_a = stop === null || stop === void 0 ? void 0 : stop.location) === null || _a === void 0 ? void 0 : _a[0]) || { address: '', name: '' };
}
function getMomentDate(date) {
    if (!date) {
        return null;
    }
    return moment(date);
}
